import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponseHeaders,
} from 'axios';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { loadingState } from '../states/loadingState';
import { alertMessage } from '../utils';
import useAuth from './useAuth';

const SERVER_URL = process.env.VITE_APP_SERVER_URL;

interface AxiosError<T = any, D = any> extends Error {
  config: AxiosRequestConfig<D> & any;
  code?: string;
  request?: any;
  response?: AxiosResponse<T, D>;
  isAxiosError: boolean;
  toJSON: () => object;
}

interface AxiosResponse<T = any, D = any> {
  data: T; // {detail: ''}
  status: number; // 401
  statusText: string;
  headers: AxiosResponseHeaders;
  config: AxiosRequestConfig<D>;
  request?: any;
}

const isAxiosError = <ResponseDataType>(
  error: unknown
): error is AxiosError<ResponseDataType> => {
  return axios.isAxiosError(error);
};

type ResponseDataType = { detail?: string; error?: string };

// asiox를 사용하여 Fetch 및 api 전역 에러 핸들링으르 위한 hook
const useAxios = () => {
  const navigate = useNavigate();
  const [, setIsLoading] = useRecoilState(loadingState);
  const {
    getRefreshToken,
    setRefreshToken,
    getAccessToken,
    setAccessToken,
    logOut,
  } = useAuth();

  const axiosInstance: AxiosInstance = axios.create({
    baseURL: SERVER_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const handleError = (error) => {
    let errorMessage;

    if (Array.isArray(error.response?.data)) {
      errorMessage = JSON.stringify(error.response?.data);
    } else {
      errorMessage = error.response?.data.detail || error.response?.data.error;
    }

    const status = error.response?.status;

    if (status === 401) {
      alertMessage(
        errorMessage
          ? `${errorMessage}\n로그인 화면으로 이동합니다.`
          : '로그아웃 되었습니다. 다시 로그인해주세요.'
      );
      logOut();
      navigate('/signin');
    } else if (status === 500) {
      alertMessage('일시적인 서버 오류입니다. 잠시 후 다시 시도해주세요.');
    } else {
      alertMessage(errorMessage || '알 수 없는 오류가 발생하였습니다.');
    }

    return Promise.reject(error);
  };

  const handleRefreshToken = async (originalRequest, error) => {
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      return handleError(error);
    }

    let data;

    try {
      const response = await axios.post(`${SERVER_URL}/users/user/refresh/`, {
        refresh: refreshToken,
      });

      data = response.data;
    } catch (refreshErr) {
      return handleError(refreshErr);
    }

    const { access, refresh } = data;

    setAccessToken(access);
    setRefreshToken(refresh);

    // 원래 요청 재시도
    return axiosInstance({
      ...originalRequest,
      headers: { Authorization: `Bearer ${access}` },
    }).catch((retryErr) => {
      return handleError(retryErr);
    });
  };

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (!isAxiosError<ResponseDataType>(error)) {
        alertMessage('알 수 없는 에러가 발생했습니다.');

        return;
      }

      const status = error.response?.status;
      const originalRequest = error.config;

      if (status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        // 리프레시 토큰으로 액세스 토큰 재발급 요청
        return handleRefreshToken(originalRequest, error);
      }

      return handleError(error);
    }
  );

  /**
   * @param {boolean} useToken 토큰(인증)이 필요한 api 요청인지 여부
   * @param {AxiosRequestConfig} configParams axiosConfig
   * @param {boolean} hideSpinner api 로딩중 노출되는 Spinner를 감출지 여부 (optional)
   */
  const requestAxios = async <T>(
    useToken: boolean,
    configParams: AxiosRequestConfig,
    hideSpinner?: boolean
  ) => {
    try {
      const accessToken = getAccessToken();

      if (useToken) {
        axiosInstance.defaults.headers.common[
          'Authorization'
        ] = `Bearer ${accessToken}`;
      } else {
        delete axios.defaults.headers.common['Authorization'];
      }

      if (!hideSpinner) {
        setIsLoading(true);
      }

      const response = await axiosInstance.request(configParams);
      return response?.data as T;
    } catch (error) {
      return Promise.reject(error);
    } finally {
      setIsLoading(false);
    }
  };

  return { requestAxios };
};

export default useAxios;
